import { useCallback, useEffect, useMemo, useState } from "react";
import useDebounce from "../../../../hooks/useDebounce";
import Button from "../../../../Components/Common/Button/Button";
import AlertBanner from "../components/AlertBanner";
import AssignmentDrawer, {
  DrawerMode,
  AssignRole,
  ProjectInspector,
  VacationCoverageEntry,
  VacationRole,
} from "../components/AssignmentDrawer";
import InspectorGrid from "../components/InspectorGrid";
import ProjectsToolbar from "../components/ProjectsToolbar";
import ProjectTable from "../components/ProjectTable";
import StatsStrip from "../components/StatsStrip";
import { useToast } from "../../../../Components/Common/Toast/ToastContext";
import ViewTabs from "../components/ViewTabs";
import {
  filterOptions,
  notifyOptions,
  viewTabs,
  LOAD_HIGH,
  LOAD_MEDIUM,
} from "../constants/inspectorAssignData";
import PlusIcon from "Assets/Img/PlusIcon";
import TableSkeleton from "../../../../Components/Common/Skeleton/TableSkeleton";
import Pagination from "../../../../Components/Common/Pagination/Pagination";
import {
  deleteInspectorLeave,
  getInspectorLeaves,
  createInspectorAssignment,
  getInspectorAssignmentProjects,
  getInspectorProjectDetails,
  removeInspectorAssignment,
  assignInspectorVacation,
  updateInspectorVacation,
  getVacationProjects,
} from "../../../../services/inspectorAssignService";

interface ApiProject extends Record<string, unknown> {
  id: string;
  name: string;
  meta?: string;
  drawInfo?: string;
  type?: string;
  status?: string;
  inspectors: ProjectInspector[];
  overdue?: boolean;
  nextInspection?: string;
}

interface ApiInspectorDetail {
  id: string;
  name: string;
  designation: string | null;
  focused: string[];
  onVacation: boolean;
  leaves: { id?: string; leaveFrom?: string; leaveTo?: string }[];
  assignedProjects: {
    projectId: string;
    projectName: string;
    role: string;
    coverFrom?: string | null;
    coverTo?: string | null;
  }[];
}

interface ApiStats {
  activeProjectCount?: number;
  registeredInspectorCount?: number;
  coverageAssignedProjectCount?: number;
  unassignedProjectCount?: number;
  fullyAssignedProjectCount?: number;
}

interface Pagination {
  total: number;
  totalPages?: number;
  limit?: number;
}

interface Filters {
  inspector: string;
  inspectorId?: string;
  type: string;
  status: string;
}

const PROJECTS_LIMIT = 10;

interface FilterParams {
  page?: number;
  limit?: number;
  q?: string;
  type?: string;
  status?: string;
  inspectorId?: string;
  isUnassigned?: boolean;
}

const defaultNotifyState = notifyOptions.reduce<Record<string, boolean>>(
  (acc, option) => {
    acc[option.id] = option.defaultChecked;
    return acc;
  },
  {},
);

const modeSuccessMessage: Record<DrawerMode, string> = {
  assign: "assigned as primary inspector",
  coverage: "added as coverage inspector",
  reassign: "reassigned to",
  vacation: "vacation saved and notifications sent for",
  assignProject: "assigned to project successfully",
};

const InspectorAssignPage = () => {
  const [activeView, setActiveView] = useState("projects");

  const [projects, setProjects] = useState<ApiProject[]>([]);
  const [apiStats, setApiStats] = useState<ApiStats | null>(null);
  const [inspectorGridData, setInspectorGridData] = useState<
    ApiInspectorDetail[]
  >([]);
  const [pagination, setPagination] = useState<Pagination>({ total: 0 });
  const [page, setPage] = useState(1);
  const [gridPage, setGridPage] = useState(1);
  const [gridPagination, setGridPagination] = useState<Pagination>({
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [loadingGrid, setLoadingGrid] = useState(false);
  const [loadingLeave, setLoadingLeave] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search.trim(), 400);
  const [filters, setFilters] = useState<Filters>({
    inspector: "All Inspectors",
    type: "All Types",
    status: "All Statuses",
  });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<DrawerMode>("assign");
  const [drawerProjectId, setDrawerProjectId] = useState("");
  const [drawerFocusInspector, setDrawerFocusInspector] = useState("");
  const [selectedInspectorId, setSelectedInspectorId] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [assignRole, setAssignRole] = useState<AssignRole>("primary");
  const [selectedReason, setSelectedReason] = useState("");
  const [coverageDates, setCoverageDates] = useState({ from: "", to: "" });
  const [editLeaveId, setEditLeaveId] = useState("");
  const [vacationCoverage, setVacationCoverage] = useState<
    Record<string, VacationCoverageEntry>
  >({});
  const [notes, setNotes] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [notifyState, setNotifyState] =
    useState<Record<string, boolean>>(defaultNotifyState);

  const showToast = useToast();

  const dynamicFilterOptions = useMemo(
    () => ({ type: filterOptions.type, status: filterOptions.status }),
    [],
  );

  const buildFilterParams = useCallback(
    (pageNum = 1): FilterParams => {
      const params: FilterParams = { page: pageNum, limit: PROJECTS_LIMIT };
      if (debouncedSearch) params.q = debouncedSearch;
      if (filters.type !== "All Types")
        params.type = filters.type.toLowerCase();
      if (filters.status !== "All Statuses")
        params.status =
          filters.status === "In Progress" ? "active" : "pre_construction";
      if (filters.inspector === "Unassigned") {
        params.isUnassigned = true;
      } else if (filters.inspectorId) {
        params.inspectorId = filters.inspectorId;
      }
      return params;
    },
    [debouncedSearch, filters],
  );

  const fetchProjects = useCallback(
    async (params: FilterParams = {}) => {
      setLoading(true);
      try {
        const response = await getInspectorAssignmentProjects(
          params as Record<string, unknown>,
        );
        const res = (response as any)?.data;
        setProjects(Array.isArray(res?.data) ? res.data : []);
        setApiStats(res?.addOns?.stats ?? null);
        setPagination(res?.pagination ?? { total: 0 });
      } catch {
        showToast("Failed to load projects");
      } finally {
        setLoading(false);
      }
    },
    [showToast],
  );

  const GRID_LIMIT = 10;

  const getInspectorProjectDetailsApi = (pageNum = 1) => {
    setLoadingGrid(true);
    getInspectorProjectDetails(pageNum, GRID_LIMIT)
      .then((response) => {
        const res = (response as any)?.data;
        if (Array.isArray(res?.data)) setInspectorGridData(res.data);
        if (res?.addOns?.stats) setApiStats(res.addOns.stats);
        setGridPagination(res?.pagination ?? { total: 0 });
        setGridPage(pageNum);
      })
      .finally(() => setLoadingGrid(false));
  };
  useEffect(() => {
    if (activeView !== "inspectors") return;
    getInspectorProjectDetailsApi();
  }, [activeView]);

  const mappedInspectors = useMemo(
    () =>
      inspectorGridData.map((inspector, i) => {
        const words = inspector.name.trim().split(" ");
        const initials =
          words.length >= 2
            ? `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase()
            : (words[0]?.[0]?.toUpperCase() ?? "?");
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const parseLocalDate = (d: string) => new Date(`${d}T00:00:00`);
        const activeLeave =
          inspector.leaves.find(
            (l) =>
              l.leaveFrom &&
              l.leaveTo &&
              today >= parseLocalDate(l.leaveFrom) &&
              today <= parseLocalDate(l.leaveTo),
          ) ?? (inspector.onVacation ? inspector.leaves[0] : undefined);
        const upcomingLeave = !activeLeave
          ? inspector.leaves.find(
              (l) => l.leaveFrom && parseLocalDate(l.leaveFrom) > today,
            )
          : undefined;
        const isOnLeave = inspector.onVacation || !!activeLeave;
        const fmtDate = (d: string) =>
          new Date(d).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          });
        const fmtMonthDay = (d: string) =>
          new Date(d).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          });
        const fmtDateRange = (from: string, to: string) => {
          const f = new Date(from),
            t = new Date(to);
          if (f.toDateString() === t.toDateString()) return fmtDate(from);
          const sameMonth =
            f.getMonth() === t.getMonth() &&
            f.getFullYear() === t.getFullYear();
          return sameMonth
            ? `${fmtMonthDay(from)}–${t.getDate()}, ${f.getFullYear()}`
            : `${fmtDate(from)}–${fmtDate(to)}`;
        };
        const count = inspector.assignedProjects.length;
        const loadClass =
          count >= LOAD_HIGH ? "high" : count >= LOAD_MEDIUM ? "medium" : "low";
        const loadWidth =
          count === 0
            ? "0%"
            : count >= LOAD_HIGH
              ? "100%"
              : count >= LOAD_MEDIUM
                ? "66.67%"
                : "33.33%";
        const notesTone = isOnLeave
          ? "warn"
          : count >= LOAD_HIGH
            ? "danger"
            : count >= LOAD_MEDIUM
              ? "medium"
              : "good";
        return {
          id: inspector.id,
          name: inspector.name,
          initials,
          avatarClass: !isOnLeave ? "avatarBlue" : "avatarAmber",
          meta: inspector.designation
            ? `${inspector.designation} Inspector`
            : "Inspector",
          focused: inspector.focused ?? "",
          hasLeave: inspector.leaves.length > 0,
          status: isOnLeave ? "vacation" : "active",
          statusLabel: isOnLeave
            ? `On Vacation${activeLeave?.leaveFrom && activeLeave.leaveTo ? ` ${fmtDateRange(activeLeave.leaveFrom, activeLeave.leaveTo)}` : ""}`
            : "Active",
          loadText: `${count} project${count !== 1 ? "s" : ""}`,
          loadClass,
          loadWidth,
          notesTone,
          notes: isOnLeave
            ? `${activeLeave?.leaveFrom && activeLeave.leaveTo ? `On vacation ${fmtDateRange(activeLeave.leaveFrom, activeLeave.leaveTo)}.` : "On vacation."} Any inspections during this period require a coverage inspector.`
            : count >= LOAD_HIGH
              ? "High load — consider redistributing before adding new projects"
              : count >= LOAD_MEDIUM
                ? "Medium load — monitor before adding new projects"
                : "Capacity available — good candidate for new project assignments",
          vacationNote:
            !isOnLeave && upcomingLeave?.leaveFrom && upcomingLeave.leaveTo
              ? `Upcoming vacation ${fmtDateRange(upcomingLeave.leaveFrom, upcomingLeave.leaveTo)}. Plan coverage in advance.`
              : undefined,
          projects: inspector.assignedProjects.map((p) => ({
            name: p.projectName,
            role: p.role === "cover" ? "coverage" : p.role,
            coverDateRange:
              p.role === "cover" && p.coverFrom && p.coverTo
                ? fmtDateRange(p.coverFrom, p.coverTo)
                : undefined,
          })),
        };
      }),
    [inspectorGridData],
  );

  useEffect(() => {
    setPage(1);
    fetchProjects(buildFilterParams(1));
  }, [buildFilterParams, fetchProjects]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchProjects(buildFilterParams(newPage));
  };

  const stats = useMemo(() => {
    const active = apiStats?.activeProjectCount ?? 0;
    const inspectorCount = apiStats?.registeredInspectorCount ?? 0;
    const coverage = apiStats?.coverageAssignedProjectCount ?? 0;
    const unassigned = apiStats?.unassignedProjectCount ?? 0;
    const fullyAssigned =
      apiStats?.fullyAssignedProjectCount ?? active - unassigned;

    return [
      { label: "Active Projects", value: active, tone: "blue" },
      { label: "Inspectors", value: inspectorCount },
      { label: "Coverage Assignments", value: coverage, tone: "amber" },
      { label: "Unassigned", value: unassigned, tone: "red" },
      { label: "Fully Assigned", value: fullyAssigned, tone: "green" },
    ];
  }, [projects, apiStats]);

  const currentProject = useMemo(
    () => projects.find((project) => project.id === drawerProjectId) ?? null,
    [projects, drawerProjectId],
  );

  const unassignedCount = stats[3].value as number;

  useEffect(() => {
    const { style } = document.body;
    const previousOverflow = style.overflow;

    if (drawerOpen) {
      style.overflow = "hidden";
    }

    return () => {
      style.overflow = previousOverflow;
    };
  }, [drawerOpen]);

  const inspectorSummary = useMemo(() => {
    if (drawerMode !== "assignProject" || !drawerFocusInspector)
      return undefined;
    const insp = inspectorGridData.find((i) => i.id === drawerFocusInspector);
    if (!insp) return undefined;
    const words = insp.name.trim().split(" ");
    const initials =
      words.length >= 2
        ? `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase()
        : (words[0]?.[0]?.toUpperCase() ?? "?");
    return { name: insp.name, initials, meta: insp.designation ?? undefined };
  }, [drawerMode, drawerFocusInspector, inspectorGridData]);

  const vacationProjects = useMemo(() => {
    if (drawerMode !== "vacation" || !drawerFocusInspector) return [];
    const insp = inspectorGridData.find((i) => i.id === drawerFocusInspector);
    return (
      insp?.assignedProjects.map((p) => ({
        id: p.projectId,
        name: p.projectName,
      })) ?? []
    );
  }, [drawerMode, drawerFocusInspector, inspectorGridData]);

  const openDrawer = (
    project: ApiProject | null,
    mode: DrawerMode,
    focusInspector = "",
  ) => {
    setDrawerMode(mode);
    setDrawerProjectId(project?.id || "");
    setDrawerFocusInspector(focusInspector);
    setSelectedInspectorId("");
    setSelectedProjectId("");
    setAssignRole(mode === "coverage" ? "coverage" : "primary");
    setSelectedReason("");
    setEditLeaveId("");
    setCoverageDates({ from: "", to: "" });
    setVacationCoverage({});
    if (mode === "vacation" && focusInspector) {
      const insp = inspectorGridData.find((i) => i.id === focusInspector);
      if (insp && (insp.onVacation || insp.leaves.length > 0)) {
        setLoadingLeave(true);
        getInspectorLeaves(insp.id)
          .then((res: any) => {
            const leave = res?.data?.data?.[0];
            if (leave) {
              setCoverageDates({
                from: leave.leaveFrom ?? "",
                to: leave.leaveTo ?? "",
              });
              setEditLeaveId(leave.id ?? "");
              if (leave.note) setNotes(leave.note);

              if (leave.id) {
                getVacationProjects(leave.id)
                  .then((projRes: any) => {
                    const projects = projRes?.data?.data;
                    if (!Array.isArray(projects)) return;
                    setVacationCoverage(
                      projects.reduce<Record<string, VacationCoverageEntry>>(
                        (acc, p) => {
                          if (p.projectId && p.inspector?.id) {
                            acc[p.projectId] = {
                              inspectorId: p.inspector.id,
                              role: p.role === "primary" ? "primary" : "coverage",
                            };
                          }
                          return acc;
                        },
                        {},
                      ),
                    );
                  })
                  .catch(() => {});
              }
            }
          })
          .finally(() => setLoadingLeave(false));
      }
    }
    setNotes("");
    setNotifyState(defaultNotifyState);
    setDrawerOpen(true);
  };

  const removeInspector = async (
    _projectId: string,
    assignmentId: string,
    _role: string,
  ) => {
    try {
      await removeInspectorAssignment(assignmentId);
      showToast("Inspector removed from project");
      fetchProjects(buildFilterParams(page));
    } catch {
      showToast("Failed to remove inspector");
    }
  };

  const saveAssignment = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      if (drawerMode === "assignProject") {
        if (!selectedProjectId) {
          showToast("Please select a project");
          return;
        }
        const insp = inspectorGridData.find(
          (i) => i.id === drawerFocusInspector,
        );
        if (!insp) {
          showToast("Inspector not found");
          return;
        }
        if (assignRole === "coverage") {
          const { from, to } = coverageDates;
          if (!from || !to) {
            showToast(
              "Please select both From and To dates for the coverage period",
            );
            return;
          }
        }

        const assignProjectPayload: Record<string, unknown> = {
          inspectorId: insp.id,
          role: assignRole === "coverage" ? "cover" : assignRole,
          note: notes || undefined,
          notifyNewInspector: notifyState.notifyNewInspector ?? false,
          notifyPrevInspector: notifyState.notifyPrevInspector ?? false,
          notifyLendingOfficer: notifyState.notifyLendingOfficer ?? false,
          notifyGc: notifyState.notifyGc ?? false,
        };
        if (assignRole === "coverage") {
          assignProjectPayload.coverFrom = new Date(
            `${coverageDates.from}T23:59:59.000Z`,
          ).toISOString();
          assignProjectPayload.coverTo = new Date(
            `${coverageDates.to}T23:59:59.000Z`,
          ).toISOString();
        }
        const res = (await createInspectorAssignment(
          selectedProjectId,
          assignProjectPayload,
        )) as any;
        if (!res?.data?.success) {
          showToast(res?.data?.message || "Failed to save assignment");
          return;
        }
        showToast("Inspector assigned to project successfully");
        setDrawerOpen(false);
        fetchProjects(buildFilterParams(page));
        getInspectorProjectDetailsApi();

        return;
      }

      if (drawerMode === "assign" && !currentProject) {
        if (!selectedProjectId) {
          showToast("Please select a project");
          return;
        }
        if (!selectedInspectorId) {
          showToast("Please select an inspector");
          return;
        }
        const res = (await createInspectorAssignment(selectedProjectId, {
          inspectorId: selectedInspectorId,
          role: "primary",
          note: notes || undefined,
          notifyNewInspector: notifyState.notifyNewInspector ?? false,
          notifyPrevInspector: notifyState.notifyPrevInspector ?? false,
          notifyLendingOfficer: notifyState.notifyLendingOfficer ?? false,
          notifyGc: notifyState.notifyGc ?? false,
        })) as any;
        if (!res?.data?.success) {
          showToast(res?.data?.message || "Failed to save assignment");
          return;
        }
        showToast("Inspector assigned to project successfully");
        setDrawerOpen(false);
        fetchProjects(buildFilterParams(page));
        getInspectorProjectDetailsApi();

        return;
      }

      if (drawerMode === "vacation") {
        const insp = inspectorGridData.find(
          (i) => i.id === drawerFocusInspector,
        );
        if (!insp) {
          showToast("Inspector not found");
          return;
        }
        const { from, to } = coverageDates;
        if (!from || !to) {
          showToast(
            "Please select both From and To dates for the vacation period",
          );
          return;
        }
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (new Date(`${from}T00:00:00`) < today) {
          showToast("Vacation start date must be today or a future date");
          return;
        }
        if (new Date(to) < new Date(from)) {
          showToast("Vacation end date must be on or after the start date");
          return;
        }
        const projectsMissingRole = vacationProjects.filter((proj) => {
          const entry = vacationCoverage[proj.id];
          return entry?.inspectorId && !entry.role;
        });
        if (projectsMissingRole.length > 0) {
          showToast(
            `Please choose Primary or Coverage for ${projectsMissingRole[0].name}`,
          );
          return;
        }

        const assignedProjects = vacationProjects.filter(
          (proj) => vacationCoverage[proj.id]?.inspectorId,
        );
        if (
          vacationProjects.length > 0 &&
          assignedProjects.length !== vacationProjects.length
        ) {
          showToast(
            "Please assign a covering inspector to every affected project before saving.",
          );
          return;
        }

        const projects =
          assignedProjects.length > 0
            ? assignedProjects.map((proj) => {
                const entry = vacationCoverage[proj.id]!;
                return {
                  projectId: proj.id,
                  inspectorId: entry.inspectorId,
                  role:
                    entry.role === "primary"
                      ? ("primary" as const)
                      : ("cover" as const),
                  notifyNewInspector: true,
                };
              })
            : undefined;

        const vacationPayload = {
          leaveFrom: from,
          leaveTo: to,
          leaveType: "personal",
          note: notes || undefined,
          ...(projects && { projects }),
        };

        const res: any = editLeaveId
          ? await updateInspectorVacation(editLeaveId, vacationPayload)
          : await assignInspectorVacation(insp.id, vacationPayload);
        if (res?.error) {
          showToast(res?.error?.message || "Failed to save vacation");
          setDrawerOpen(false);
          return;
        }

        showToast("Vacation saved successfully");
        setDrawerOpen(false);
        fetchProjects(buildFilterParams(page));
        getInspectorProjectDetailsApi();
        return;
      }

      if (!selectedInspectorId) {
        showToast("Please select an inspector");
        return;
      }
      if (!currentProject) {
        showToast("No project selected");
        return;
      }

      const payload: Record<string, unknown> = {
        inspectorId: selectedInspectorId,
        role:
          drawerMode === "reassign"
            ? "primary"
            : assignRole === "coverage"
              ? "cover"
              : assignRole,
        note: notes || undefined,
        notifyNewInspector: notifyState.notifyNewInspector ?? false,
        notifyPrevInspector: notifyState.notifyPrevInspector ?? false,
        notifyLendingOfficer: notifyState.notifyLendingOfficer ?? false,
        notifyGc: notifyState.notifyGc ?? false,
      };

      if (drawerMode === "reassign") {
        if (!selectedReason) {
          showToast("Please select a reason for reassignment");
          return;
        }
        payload.removeReason = selectedReason;
      }

      if (drawerMode === "coverage" && assignRole === "coverage") {
        const { from, to } = coverageDates;
        if (!from || !to) {
          showToast(
            "Please select both From and To dates for the coverage period",
          );
          return;
        }
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const fromDate = new Date(from);
        const toDate = new Date(to);
        if (fromDate < today) {
          showToast("Cover From date must be today or a future date");
          return;
        }
        if (toDate < today) {
          showToast("Cover To date must be today or a future date");
          return;
        }
        if (toDate <= fromDate) {
          showToast("Cover To date must be after the Cover From date");
          return;
        }
        payload.coverFrom = new Date(`${from}T23:59:59.000Z`).toISOString();
        payload.coverTo = new Date(`${to}T23:59:59.000Z`).toISOString();
      }

      const assignRes = (await createInspectorAssignment(
        currentProject.id,
        payload,
      )) as any;
      if (!assignRes?.data?.success) {
        showToast(assignRes?.data?.message || "Failed to save assignment");
        return;
      }

      showToast(
        `Inspector ${modeSuccessMessage[drawerMode] || "assigned"} successfully`,
      );
      setDrawerOpen(false);
      fetchProjects(buildFilterParams(page));
    } catch {
      showToast("Failed to save assignment");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="inspectorAssignPage">
      <main className="page">
        <div className="pageHeader">
          <div>
            <h1>Inspector Assignment</h1>
            <p>
              Assign, reassign, and manage inspector coverage across all active
              projects. One primary inspector per project; add coverage for
              vacations or handoffs.
            </p>
          </div>
          <Button
            className="btn btnPrimary"
            onClick={() => openDrawer(null, "assign")}
          >
            <PlusIcon /> Assign Inspector
          </Button>
        </div>

        <AlertBanner
          unassignedCount={unassignedCount}
          onShowUnassigned={() => {
            setFilters((prev) => ({
              ...prev,
              inspector: "Unassigned",
              inspectorId: undefined,
            }));
            setActiveView("projects");
          }}
        />

        <StatsStrip stats={stats} />

        <ViewTabs
          tabs={viewTabs}
          activeView={activeView}
          onChange={setActiveView}
        />

        {activeView === "projects" ? (
          <>
            <ProjectsToolbar
              search={search}
              filters={filters}
              options={dynamicFilterOptions}
              onSearch={setSearch}
              onChangeFilter={(key, value) =>
                setFilters((prev) => ({ ...prev, [key]: value }))
              }
              onChangeInspector={(name, id) =>
                setFilters((prev) => ({
                  ...prev,
                  inspector: name,
                  inspectorId: id,
                }))
              }
            />

            {loading ? (
              <TableSkeleton cols={6} rows={8} />
            ) : (
              <>
                <ProjectTable
                  projects={projects}
                  isFiltered={
                    !!debouncedSearch ||
                    filters.type !== "All Types" ||
                    filters.status !== "All Statuses" ||
                    filters.inspector !== "All Inspectors"
                  }
                  onOpenDrawer={openDrawer}
                  onRemoveInspector={removeInspector}
                />
                <Pagination
                  currentPage={page}
                  totalPages={pagination.totalPages ?? 1}
                  total={pagination.total}
                  limit={PROJECTS_LIMIT}
                  onPageChange={handlePageChange}
                />
              </>
            )}
          </>
        ) : (
          <>
            <InspectorGrid
              inspectors={mappedInspectors}
              loading={loadingGrid}
              onOpenDrawer={openDrawer}
            />
            <Pagination
              currentPage={gridPage}
              totalPages={gridPagination.totalPages ?? 1}
              total={gridPagination.total}
              limit={GRID_LIMIT}
              onPageChange={(p) => getInspectorProjectDetailsApi(p)}
            />
          </>
        )}
      </main>

      <AssignmentDrawer
        open={drawerOpen}
        mode={drawerMode}
        project={currentProject}
        focusInspector={
          inspectorGridData.find((i) => i.id === drawerFocusInspector)?.name ??
          ""
        }
        inspectorSummary={inspectorSummary}
        selectedProjectId={selectedProjectId}
        onSelectProject={setSelectedProjectId}
        assignedProjectIds={
          drawerMode === "assignProject" && drawerFocusInspector
            ? (inspectorGridData
                .find((i) => i.id === drawerFocusInspector)
                ?.assignedProjects.map((p) => p.projectId) ?? [])
            : []
        }
        assignedInspectorIds={
          currentProject?.inspectors?.reduce<string[]>((acc, i) => {
            if (i.inspector?.id) acc.push(i.inspector.id);
            return acc;
          }, []) ?? []
        }
        selectedInspectorId={selectedInspectorId}
        assignRole={assignRole}
        selectedReason={selectedReason}
        coverageDates={coverageDates}
        notes={notes}
        notifyState={notifyState}
        vacationInspectorId={
          drawerMode === "vacation" ? drawerFocusInspector : undefined
        }
        vacationProjects={vacationProjects}
        vacationCoverage={vacationCoverage}
        onVacationCoverageChange={(projectId, inspectorId) =>
          setVacationCoverage((prev) => ({
            ...prev,
            [projectId]: {
              ...prev[projectId],
              inspectorId,
              role: prev[projectId]?.role ?? "coverage",
            },
          }))
        }
        onVacationRoleChange={(projectId, role: VacationRole) =>
          setVacationCoverage((prev) => ({
            ...prev,
            [projectId]: {
              inspectorId: prev[projectId]?.inspectorId ?? "",
              role,
            },
          }))
        }
        onClose={() => setDrawerOpen(false)}
        onSelectInspector={setSelectedInspectorId}
        onSetAssignRole={setAssignRole}
        onSelectReason={setSelectedReason}
        onCoverageDateChange={(key, value) =>
          setCoverageDates((prev) => ({ ...prev, [key]: value }))
        }
        onNotesChange={setNotes}
        onToggleNotify={(id) =>
          setNotifyState((prev) => ({ ...prev, [id]: !prev[id] }))
        }
        loadingLeave={loadingLeave}
        onSave={saveAssignment}
        onDeleteLeave={
          drawerMode === "vacation" && editLeaveId
            ? async () => {
                try {
                  await deleteInspectorLeave(editLeaveId);
                  showToast("Vacation cancelled successfully");
                  setDrawerOpen(false);
                  getInspectorProjectDetailsApi();
                } catch {
                  showToast("Failed to cancel vacation");
                }
              }
            : undefined
        }
        isSaving={isSaving}
      />
    </div>
  );
};

export default InspectorAssignPage;
